import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faXmark,
  faRightFromBracket,
  faChalkboardTeacher,
  faCalendarAlt,
  faCertificate,
  faTrophy,
  faGear,
  faSun,
  faMoon,
  faBirthdayCake,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './Layout.css';

Modal.setAppElement('#root');

const API = process.env.REACT_APP_API_BASE_URL;
const LOGO_DARK = '/assets/img/logo/logowhitenew.png';
const LOGO_LIGHT = '/assets/img/logo/logonew.png';
const THEME_KEY = 'rm-demo-theme'; // same key as the Trial Dashboard, so the choice carries over
const BDAY_SKIP_KEY = 'rm-bday-skipped'; // "Skip for now" lasts until the browser tab is closed

const NAV = [
  { to: '/student/dashboard', icon: faHome, label: 'Dashboard' },
  { to: '/student/myclasses', icon: faChalkboardTeacher, label: 'My Classes' },
  { to: '/student/schedule', icon: faCalendarAlt, label: 'My Schedule' },
  { to: '/student/certificates', icon: faCertificate, label: 'My Certificates' },
  { to: '/student/competitions', icon: faTrophy, label: 'Competitions' },
  { to: '/student/settings', icon: faGear, label: 'Settings' },
];

const safeGet = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {
    // private mode: not remembered
  }
};

const readToken = () => {
  try {
    const raw = localStorage.getItem('userData');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const StudentLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [theme, setTheme] = useState(() => (safeGet(localStorage, THEME_KEY) === 'dark' ? 'dark' : 'light'));
  const [userData, setUserData] = useState(null);

  // Birthday prompt
  const [showBirthday, setShowBirthday] = useState(false);
  const [birthday, setBirthday] = useState('');
  const [savingBirthday, setSavingBirthday] = useState(false);
  const [birthdayError, setBirthdayError] = useState('');

  const isLight = theme === 'light';
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
  const [token] = useState(readToken); // read once (stable between renders)
  const [compCount, setCompCount] = useState(0); // challenges open or coming (menu badge)
  const [trophies, setTrophies] = useState(0); // competitions won (next to the photo)

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    // one call: the menu number (open or coming challenges) and the trophies
    fetch(`${API}/api/student/competitions`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setCompCount((d.competitions || []).filter((c) => c.status === 'open' || c.status === 'upcoming').length);
        setTrophies((d.wins || []).length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ---------- Student profile (same endpoint) ----------
  const fetchUserData = useCallback(async () => {
    const response = await fetch(`${API}/api/student/layout`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Unauthorized');
    const data = await response.json();
    const profile = {
      id: data.id,
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      Image: data.Image,
      fullName: [data.firstName, data.lastName].filter(Boolean).join(' '),
      birthday: data.birthday,
    };
    setUserData(profile);
    return profile;
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    fetchUserData()
      .then((profile) => {
        if (cancelled) return;
        if (!profile.birthday && !safeGet(sessionStorage, BDAY_SKIP_KEY)) setShowBirthday(true);
      })
      .catch((error) => {
        console.error('Failed to fetch user data:', error);
        if (!cancelled) navigate('/login', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [token, fetchUserData, navigate]);

  // ---------- Heartbeat (same endpoint), now and every 30s ----------
  const studentId = userData?.id;
  useEffect(() => {
    if (!token || !studentId) return undefined;
    const sendHeartbeat = () =>
      fetch(`${API}/api/auth/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ IdStudent: studentId }),
      }).catch((error) => console.error('Heartbeat failed:', error));

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(intervalId);
  }, [token, studentId]);

  // Close the mobile menu when the page changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const next = isLight ? 'dark' : 'light';
    setTheme(next);
    safeSet(localStorage, THEME_KEY, next);
  };

  const handleLogout = () => {
    // offline right away (shared laptops: the next person who logs in shows correctly)
    if (token) {
      fetch(`${API}/api/auth/verify/offline`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true }).catch(() => {});
    }
    localStorage.removeItem('userData');
    sessionStorage.removeItem('demoStudentId');
    sessionStorage.removeItem(BDAY_SKIP_KEY);
    navigate('/login', { replace: true });
  };

  // ---------- Birthday (same endpoint) ----------
  const handleBirthdaySubmit = async (e) => {
    e.preventDefault();
    if (!birthday) {
      setBirthdayError('Please choose the date of birth.');
      return;
    }
    setSavingBirthday(true);
    setBirthdayError('');
    try {
      const response = await axios.put(
        `${API}/api/student/studentsettings/birthday`,
        { studentId: userData.id, dateOfBirth: birthday },
        { headers: authHeaders }
      );
      if (response.data.success) {
        await fetchUserData();
        setShowBirthday(false);
        setBirthday('');
      } else {
        setBirthdayError(response.data.message || 'We couldn’t save it. Please try again.');
      }
    } catch (error) {
      console.error('Error saving birthday:', error);
      setBirthdayError('We couldn’t save it. Please try again.');
    } finally {
      setSavingBirthday(false);
    }
  };

  const skipBirthday = () => {
    safeSet(sessionStorage, BDAY_SKIP_KEY, '1'); // don't ask again on every page
    setShowBirthday(false);
  };

  const fullName = userData?.fullName || '';
  const initials =
    (fullName || 'S')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('') || 'S';

  const helpUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    `Hi! I'm ${fullName || 'a RoboMind student'} and I need help with my account.`
  )}&type=phone_number&app_absent=0`;

  // Pass userData to the page, like before
  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { userData }) : child
  );

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="rmsl" data-theme={theme}>
      {/* ---------- Top bar ---------- */}
      <header className="rmsl-top">
        <div className="rmsl-top-left">
          <button
            type="button"
            className="rmsl-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="rmsl-side"
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
            {!menuOpen && compCount > 0 && <i className="rmsl-menu-dot" aria-label={`${compCount} competitions`} />}
          </button>
          <Link to="/student/dashboard" className="rmsl-logo" aria-label="Dashboard">
            <img src={isLight ? LOGO_LIGHT : LOGO_DARK} alt="RoboMind" />
          </Link>
        </div>

        <div className="rmsl-top-right">
          <button
            type="button"
            className="rmsl-theme-btn"
            onClick={toggleTheme}
            aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            title={isLight ? 'Dark mode' : 'Light mode'}
          >
            <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
          </button>

          <Link to="/student/settings" className="rmsl-user" aria-label="Your settings">
            <span className="rmsl-avatar-wrap">
              <span className="rmsl-avatar" aria-hidden="true">
                {userData?.Image ? <img src={userData.Image} alt="" /> : initials}
              </span>
              {trophies > 0 && (
                <span className="rmsl-trophy" title={`${trophies} competition ${trophies === 1 ? 'trophy' : 'trophies'}`} aria-label={`${trophies} competition trophies`}>
                  🏆{trophies > 1 ? <b>{trophies}</b> : null}
                </span>
              )}
            </span>
            <span className="rmsl-user-name">{fullName || 'Student'}</span>
          </Link>
        </div>
      </header>

      {/* ---------- Sidebar (drawer on mobile) ---------- */}
      <aside id="rmsl-side" className={`rmsl-side ${menuOpen ? 'is-open' : ''}`} aria-label="Student menu">
        <nav>
          <ul className="rmsl-nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={({ isActive }) => `rmsl-nav-item ${isActive ? 'is-active' : ''}`}>
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                  {item.to === '/student/competitions' && compCount > 0 && (
                    <b className="rmsl-nav-badge" aria-label={`${compCount} competitions`}>{compCount}</b>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="rmsl-side-bottom">
          <div className="rmsl-help">
            <strong>Need help?</strong>
            <span>Schedule changes, Zoom or payments.</span>
            <a href={helpUrl} target="_blank" rel="noopener noreferrer" className="rmsl-help-btn">
              <FontAwesomeIcon icon={faWhatsapp} /> Message us
            </a>
          </div>

          <button type="button" className="rmsl-leave" onClick={() => setShowLogout(true)}>
            <FontAwesomeIcon icon={faRightFromBracket} />
            Log out
          </button>
        </div>
      </aside>

      {menuOpen && <button type="button" className="rmsl-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      {/* ---------- Page ---------- */}
      <main className="rmsl-main">{childrenWithProps}</main>

      {/* ---------- Logout confirmation ---------- */}
      <Modal
        isOpen={showLogout}
        onRequestClose={() => setShowLogout(false)}
        contentLabel="Log out"
        className={`rmsl-modal ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmsl-modal-overlay"
      >
        <h2 className="rmsl-modal-title">Log out?</h2>
        <p className="rmsl-modal-text">You can sign in again any time with your username and password.</p>
        <div className="rmsl-modal-actions">
          <button type="button" className="rmsl-modal-cancel" onClick={() => setShowLogout(false)}>
            Stay
          </button>
          <button type="button" className="rmsl-modal-confirm" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </Modal>

      {/* ---------- Birthday prompt (only when missing) ---------- */}
      <Modal
        isOpen={showBirthday}
        onRequestClose={skipBirthday}
        shouldCloseOnOverlayClick={false}
        contentLabel="Add your date of birth"
        className={`rmsl-modal rmsl-bday ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmsl-modal-overlay"
      >
        <span className="rmsl-bday-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBirthdayCake} />
        </span>
        <h2 className="rmsl-modal-title">Welcome{userData?.firstName ? `, ${userData.firstName}` : ''}!</h2>
        <p className="rmsl-modal-text">Add your date of birth so we can match your lessons and progress to your age.</p>

        <form onSubmit={handleBirthdaySubmit} className="rmsl-bday-form" noValidate>
          <label htmlFor="rmsl-bday-input" className="rmsl-bday-label">
            Date of birth
          </label>
          <input
            id="rmsl-bday-input"
            type="date"
            className="rmsl-bday-input"
            value={birthday}
            max={today}
            onChange={(e) => {
              setBirthday(e.target.value);
              setBirthdayError('');
            }}
            aria-invalid={Boolean(birthdayError)}
            aria-describedby={birthdayError ? 'rmsl-bday-error' : undefined}
          />
          {birthdayError && (
            <p id="rmsl-bday-error" className="rmsl-bday-error" role="alert">
              {birthdayError}
            </p>
          )}

          <div className="rmsl-modal-actions">
            <button type="button" className="rmsl-bday-skip" onClick={skipBirthday} disabled={savingBirthday}>
              Skip for now
            </button>
            <button type="submit" className="rmsl-modal-cancel" disabled={savingBirthday}>
              {savingBirthday ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="rmsl-bday-note">You can change it later in Settings.</p>
        </form>
      </Modal>
    </div>
  );
};

export default StudentLayout;